// health-info.ts — client-side render of the creature health/vitality panel,
// reading from window.FateTide.casterCache (no DB ping on a cache hit).
//
// Replaces options/tools/encounter_creatures/encounter_creature_info/
// creature_health_info_inc.php. Two modes (mirrors the PHP):
//   - In-encounter: current/temp/max vitality stats + Status, plus a
//     permission-gated edit block (V slider + Adjust/Heal/Resurrect/Temp/Max
//     buttons). The V buttons go to the resource truth seam (websocket/resource-truth.ts
//     ftResourceAdjustTruth — the browser decides, resource_truth_commit_inc.php lazily
//     saves; N6 2026-10-01: the legacy adjusts/*.php endpoints are gone); Resurrect goes
//     to creature-actions resurrectCreature (the status truth lane).
//   - Template (not in an encounter): V Dice / V Die / V-from-Body / Roll V /
//     Average V / Max V. Fixes the old bug where v_dice was read from
//     computed.vitality (it isn't there) — it's a creature LAYER (ftLayerValue).

import { hasPlayerPermission } from '../shared/array-types';
import { setActiveInfoPanel } from './active-panel';
import type { CachedCaster, ItemLikeRecord } from './types';

const HEALTH_KINDS = new Set(['v', 'heal_to_max', 'temp_v', 'max_temp_v', 'max_v']);
type HealthInfoWindow = Window & {
    resurrectCreature?: (encounterCreatureId: string) => unknown;
    ftResourceAdjustTruth?: (kind: string, actorId: string, deltaInputId: string | null, opts?: { rollPrivate?: number }) => Promise<unknown>;
    ftGetEncounterContext?: () => { roll_private?: unknown } | null;
};

function healthAttrs(action: string, attrs: Record<string, string> = {}): string {
    const pairs = [`data-ft-health-action="${escapeHtml(action)}"`];
    for (const [key, value] of Object.entries(attrs)) {
        pairs.push(`data-ft-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}="${escapeHtml(value)}"`);
    }
    return pairs.join(' ');
}

function syncVitalitySliderFromRange(): void {
    const baseline = parseInt(String((document.getElementById('ec_v_current_baseline') as HTMLInputElement | null)?.value ?? '0'), 10) || 0;
    const range = document.getElementById('ec_v_current_range') as HTMLInputElement | null;
    if (!range) return;
    const target = parseInt(range.value, 10) || 0;
    const delta = target - baseline;
    const deltaInput = document.getElementById('input_number_v_heal_harm') as HTMLInputElement | null;
    if (deltaInput) deltaInput.value = String(delta);
    const label = document.getElementById('ec_v_current_range_label');
    if (label) label.textContent = String(target);
}

function syncVitalitySliderFromDelta(): void {
    const baseline = parseInt(String((document.getElementById('ec_v_current_baseline') as HTMLInputElement | null)?.value ?? '0'), 10) || 0;
    const deltaInput = document.getElementById('input_number_v_heal_harm') as HTMLInputElement | null;
    let delta = parseInt(String(deltaInput?.value ?? '0'), 10);
    if (Number.isNaN(delta)) delta = 0;
    const range = document.getElementById('ec_v_current_range') as HTMLInputElement | null;
    if (!range) return;
    const max = parseInt(range.max, 10) || 0;
    const target = Math.min(max, Math.max(0, baseline + delta));
    range.value = String(target);
    const label = document.getElementById('ec_v_current_range_label');
    if (label) label.textContent = String(target);
}

document.addEventListener('input', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-health-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftHealthAction ?? '';
    if (action === 'sync-range') syncVitalitySliderFromRange();
    else if (action === 'sync-delta') syncVitalitySliderFromDelta();
});

document.addEventListener('click', (event) => {
    const el = event.target instanceof Element
        ? event.target.closest<HTMLElement>('[data-ft-health-action]')
        : null;
    if (!el || !document.contains(el)) return;
    const action = el.dataset.ftHealthAction ?? '';
    const w = window as HealthInfoWindow;
    if (action === 'adjust-health') {
        const kind = el.dataset.ftKind ?? '';
        const encounterCreatureId = el.dataset.ftEncounterCreatureId ?? '';
        if (HEALTH_KINDS.has(kind) && encounterCreatureId !== '') {
            event.preventDefault();
            // The resource truth seam (websocket/resource-truth.ts): the browser decides against the actor's row — held, its encounter
            // loaded, or the encounter-less row itself — and resource_truth_commit_inc.php lazily saves. Never a legacy post (N6).
            const rollPrivate = Number(w.ftGetEncounterContext?.()?.roll_private ?? 0) || 0;
            if (typeof w.ftResourceAdjustTruth !== 'function') { displayWarning('Adjusting vitality is not available on this page — reload and try again.'); return; }
            void w.ftResourceAdjustTruth(kind, encounterCreatureId, kind === 'heal_to_max' ? null : 'input_number_v_heal_harm', { rollPrivate });
        }
    } else if (action === 'resurrect') {
        const encounterCreatureId = el.dataset.ftEncounterCreatureId ?? '';
        if (encounterCreatureId !== '') {
            event.preventDefault();
            w.resurrectCreature?.(encounterCreatureId);
            window.setTimeout(() => {
                document.getElementById('div_v_current')?.click();
            }, 3000);
        }
    } else if (action === 'roll-vitality-preview') {
        event.preventDefault();
        rollVitalityPreview(
            Number(el.dataset.ftDice ?? '0'),
            Number(el.dataset.ftDie ?? '0'),
            Number(el.dataset.ftMod ?? '0'),
            el.dataset.ftResultId ?? '',
        );
    }
});

function unwrap(v: unknown): unknown {
    return Array.isArray(v) ? v[0] : v;
}
function vNum(v: unknown): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}
/** PHP display_value_or_dash twin: '—' for 0/empty, else the value. */
function dashIfZero(v: unknown): string {
    const n = Number(v);
    return (!Number.isFinite(n) || n === 0) ? '—' : String(n);
}

/** computed.vitality.v_status is a {items:{item_id}} shell (or bare UUID). Return the UUID. */
function vStatusId(caster: CachedCaster): string {
    const raw: unknown = caster.computed?.vitality?.v_status
        ?? caster.computed?.creature?.vitality?.v_status;
    if (raw == null) return '';
    if (typeof raw === 'object') {
        const obj = raw as ItemLikeRecord;
        return String(obj.items?.item_id ?? (Array.isArray(raw) ? raw[0] : '') ?? '');
    }
    return String(raw);
}

function renderInEncounter(caster: CachedCaster, casterId: string): string {
    const vCurrent       = vNum(ftComputedValue(caster, 'vitality', 'v_current'));
    const vTemp          = ftComputedValue(caster, 'vitality', 'v_temp');
    const vMax           = vNum(ftComputedValue(caster, 'vitality', 'v_max'));
    const vMaxTemp       = vNum(ftComputedValue(caster, 'vitality', 'v_max_temp'));
    const vMaxReduction  = vNum(ftComputedValue(caster, 'vitality', 'v_max_reduction'));
    const effectiveVMaxRaw = ftComputedValue(caster, 'vitality', 'effective_v_max');
    const effectiveVMax  = (effectiveVMaxRaw != null && effectiveVMaxRaw !== '') ? vNum(effectiveVMaxRaw) : vMax;

    const ecArr = (caster.encounter_creatures ?? {}) as Record<string, unknown>;
    const encounterCreatureId = String(unwrap(ecArr.encounter_creature_id) ?? '').trim();
    const dead = vNum(unwrap(ecArr.encounter_creature_dead)) === 1;

    const showAdjust = hasPlayerPermission(caster) && encounterCreatureId !== '';

    let rows = `
        <tr><th scope="row" class="align-middle">Current V:</th>
            <td class="text-center"><h3><span class="badge bg-info">${vCurrent}</span></h3></td></tr>
        <tr><th scope="row" class="align-middle">Temp V:</th>
            <td class="text-center"><h3><span class="badge bg-warning">${escapeHtml(dashIfZero(vTemp))}</span></h3></td></tr>
        <tr><th scope="row" class="align-middle">Base Max V:</th>
            <td class="text-center"><h3><span class="badge bg-secondary">${vMax}</span></h3></td></tr>`;
    if (vMaxTemp !== 0) {
        rows += `<tr><th scope="row" class="align-middle">Temp Max V Bonus:</th>
            <td class="text-center"><h3><span class="badge bg-success">+${vMaxTemp}</span></h3></td></tr>`;
    }
    if (vMaxReduction !== 0) {
        rows += `<tr><th scope="row" class="align-middle">${iconMap('blighted')} Blighted Reduction:</th>
            <td class="text-center"><h3><span class="badge bg-danger">-${vMaxReduction}</span></h3></td></tr>`;
    }
    rows += `<tr><th scope="row" class="align-middle fw-bold">Effective Max V:</th>
            <td class="text-center"><h3><span class="badge bg-primary">${effectiveVMax}</span></h3></td></tr>`;

    let html = `
    <table class="table border mb-3">
        <thead><tr><th scope="col" colspan="2" class="table-primary align-middle">Creature Health:</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
            <tr><th scope="row" class="align-middle">Status:</th>
                <td class="text-center"><h3><span class="badge bg-danger">${iconMap(vStatusId(caster) || 'healthy')}</span></h3></td></tr>
        </tfoot>
    </table>`;

    if (showAdjust) {
        const vMaxEff = Math.max(0, effectiveVMax);
        const sliderVal = vMaxEff > 0 ? Math.min(Math.max(0, vCurrent), vMaxEff) : 0;
        const ecId = escapeHtml(encounterCreatureId);

        const resurrectBtn = (vCurrent <= 0 || dead)
            ? `<button class="btn btn-success" ${healthAttrs('resurrect', { encounterCreatureId })}>${iconMap('resurrect_creature')}</button>`
            : '';

        html += `
        <table class="table border mb-3">
            <tbody>
                <tr><td colspan="2">
                    <input type="hidden" id="ec_v_current_baseline" value="${vCurrent}">
                    <div class="form-group mb-3">
                        <h5 class="text-center mb-2">${iconMap('vitality')} Set current vitality</h5>
                        <div class="d-flex align-items-center px-1">
                            <span class="small text-muted mr-2">0</span>
                            <input type="range" id="ec_v_current_range" class="form-range flex-grow-1 mx-1" min="0" max="${vMaxEff}" step="1" value="${sliderVal}" aria-label="Set current vitality" ${healthAttrs('sync-range')}>
                            <span class="small text-muted ml-2">${vMaxEff}</span>
                        </div>
                        <div class="text-center small text-muted" id="ec_v_current_range_label">${sliderVal}</div>
                        <p class="small text-muted text-center mb-0">Drag to your target current V, then tap Adjust Vitality.</p>
                    </div>
                    <div class="form-group">
                        <h5 class="text-center">Adjust (delta):</h5>
                        <input id="input_number_v_heal_harm" class="form-control text-center" type="number" value="0" ${healthAttrs('sync-delta')}>
                    </div>
                </td></tr>
                <tr><td class="text-center">
                    <div class="d-flex align-items-center justify-content-center flex-wrap gap-2">
                        <button class="btn btn-primary" ${healthAttrs('adjust-health', { kind: 'v', encounterCreatureId })}>${iconMap('vitality')} Adjust Vitality</button>
                        <button class="btn btn-primary" ${healthAttrs('adjust-health', { kind: 'heal_to_max', encounterCreatureId })}>${iconMap('heal_to_v_max')}</button>
                        ${resurrectBtn}
                    </div>
                </td></tr>
                <tr><td class="text-center">
                    <button class="btn btn-primary" ${healthAttrs('adjust-health', { kind: 'temp_v', encounterCreatureId })}>${iconMap('temporary_vitality')} Adjust Temp Vitality</button>
                </td></tr>
                <tr><td class="text-center">
                    <button class="btn btn-primary" ${healthAttrs('adjust-health', { kind: 'max_temp_v', encounterCreatureId })}>${iconMap('max_temp_vitality')} Adjust Temp Max Vitality</button>
                </td></tr>
                <tr><td class="text-center">
                    <button class="btn btn-primary" ${healthAttrs('adjust-health', { kind: 'max_v', encounterCreatureId })}>${iconMap('max_vitality')} Adjust Max Vitality</button>
                </td></tr>
            </tbody>
        </table>`;
    }

    return html;
}

function renderTemplate(caster: CachedCaster): string {
    // v_dice is a creature LAYER (item_layer_types), NOT in computed.vitality.
    const vDiceRaw = ftLayerValue(caster, 'v_dice');
    const vDiceDisplay = (vDiceRaw == null || vDiceRaw === '') ? '?' : String(vDiceRaw);
    const vDice = vNum(vDiceRaw);
    const vDie  = ftComputedValue(caster, 'vitality', 'v_die');
    const vDieNum = vNum(vDie);
    const vDieDisplay = (vDie == null || vDie === '') ? '?' : String(vDie);
    const vAvg  = ftComputedValue(caster, 'vitality', 'v_avg');
    const vMaxFromDice = ftComputedValue(caster, 'vitality', 'v_max_from_v_dice');
    const body = vNum(caster.computed?.creature?.essences?.body?.total
        ?? caster.computed?.essences?.body?.total ?? 0);
    const sizeId = String(ftLayerValue(caster, 'size') ?? '') || 'medium';
    const bodyBonus = vDice * body;

    return `
    <table class="table border mb-3">
        <thead><tr><th scope="col" colspan="2" class="table-primary align-middle">Creature Health:</th></tr></thead>
        <tbody>
            <tr><th scope="row" class="align-middle">V Dice:</th>
                <td class="text-center"><h3><span class="badge bg-info">${escapeHtml(vDiceDisplay)}</span></h3></td></tr>
            <tr><th scope="row" class="align-middle">V Die | ${iconMap(sizeId)} :</th>
                <td class="text-center"><h3><span class="badge bg-warning">${escapeHtml(vDieDisplay)}</span></h3></td></tr>
            <tr><th scope="row" class="align-middle">V from ${iconMap('body')}:</th>
                <td class="text-center"><h3><span class="badge bg-dark">${bodyBonus}</span></h3></td></tr>
            <tr><th scope="row" class="align-middle">Roll V:</th>
                <td class="text-center">
                    <button type="button" class="btn btn-primary" ${healthAttrs('roll-vitality-preview', { dice: String(vDice), die: String(vDieNum), mod: String(bodyBonus), resultId: 'ft_v_roll_result' })}>
                        ${vDice}d${vDieNum} + ${bodyBonus}
                    </button>
                    <div id="ft_v_roll_result" class="mt-2"></div>
                </td></tr>
            <tr><th scope="row" class="align-middle">Average V:</th>
                <td class="text-center"><h3><span class="badge bg-success">${escapeHtml((vAvg == null || vAvg === '') ? '?' : String(vAvg))}</span></h3></td></tr>
            <tr><th scope="row" class="align-middle">Max V:</th>
                <td class="text-center"><h3><span class="badge bg-danger">${escapeHtml((vMaxFromDice == null || vMaxFromDice === '') ? '?' : String(vMaxFromDice))}</span></h3></td></tr>
        </tbody>
    </table>`;
}

function renderHealth(caster: CachedCaster, casterId: string): string {
    const ecArr = caster.encounter_creatures;
    const isInEncounter = ecArr != null
        && (Array.isArray(ecArr) ? ecArr.length > 0 : Object.keys(ecArr as object).length > 0);
    return isInEncounter ? renderInEncounter(caster, casterId) : renderTemplate(caster);
}

/**
 * Client-side V-dice PREVIEW roll for a template creature (not in an encounter, so it
 * has no campaign/encounter scope to log into). Rolls `dice`×d`die` + `mod` in the browser
 * and shows the result inline — no DB ping, works on any page. Attached to window so the
 * Roll-V button onclick can call it.
 */
export function rollVitalityPreview(dice: number, die: number, mod: number, resultElId: string): void {
    const el = document.getElementById(resultElId);
    if (!el) {
        return;
    }
    const d = Math.max(0, Math.floor(Number(dice) || 0));
    const sides = Math.max(0, Math.floor(Number(die) || 0));
    const bonus = Math.floor(Number(mod) || 0);
    if (d === 0 || sides === 0) {
        el.innerHTML = '<span class="text-muted small">No vitality dice to roll.</span>';
        return;
    }
    const rolls: number[] = [];
    let sum = 0;
    for (let i = 0; i < d; i++) {
        const r = Math.floor(Math.random() * sides) + 1;
        rolls.push(r);
        sum += r;
    }
    const total = sum + bonus;
    const modStr = bonus >= 0 ? `+${bonus}` : `${bonus}`;
    el.innerHTML =
        `<h3 class="mb-1"><span class="badge bg-dark border">${total}</span></h3>` +
        `<span class="text-muted small">${d}d${sides} ${escapeHtml(modStr)} &mdash; [${escapeHtml(rolls.join(', '))}]${bonus !== 0 ? ' ' + escapeHtml(modStr) : ''}</span>`;
}

/** Entry point. Repointed onclick: the V bar in build_v_mana_ecac_luck_bars_inc.php. */
export async function renderCreatureHealthInfoPanel(
    casterId: string,
    targetId = 'div_refresh_side_info',
): Promise<void> {
    const target = document.getElementById(targetId);
    if (!target) {
        return;
    }

    let caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-muted">Loading…</div>';
        try {
            await fetchAndCacheCaster(casterId);
        } catch {
            // fetchAndCacheCaster swallows its own errors.
        }
        caster = window.FateTide?.casterCache?.get(casterId) as CachedCaster | undefined;
    }

    if (!caster) {
        target.innerHTML = '<div class="p-3 text-center text-danger">Could not load creature data.</div>';
        return;
    }

    const render = (c: CachedCaster) => renderHealth(c, casterId);
    setActiveInfoPanel(casterId, targetId, render);
    target.innerHTML = render(caster);
}
